import type { Tier } from '@/analysis/wsTypes'
import { fmtKbps, fmtLagMs, fmtRatio, getDemuxInRateText } from '@/components/table/format'

export type MetricKey =
  | 'demuxInRate'
  | 'demuxToDecodeLagMs'
  | 'renderRatio'
  | 'downloadKbps'
  | 'e2eLatency'

export type MetricLayer = {
  key: MetricKey
  label: string
  tier: Tier | null
  valueText: string | null
}

/**
 * Hex layer value text. Delegates to the shared table formatters so the hex,
 * tables and world tooltip cannot drift apart — this used to be a private copy
 * that rendered demux as "30.0/s" and render in/out as a percentage while the
 * tables showed "fps" and a decimal for the same numbers.
 *
 * Demux rate is the exception: the hex layer shows the §3.2.E state as a word
 * rather than a number, since the layer already carries the severity colour and
 * the state is what that colour means.
 */
function fmt(v: number | null, key: MetricKey, tier?: Tier | null): string | null {
  if (key === 'demuxInRate') return tier == null ? null : getDemuxInRateText(tier)
  if (v === null || !Number.isFinite(v)) return null
  if (key === 'demuxToDecodeLagMs' || key === 'e2eLatency') return fmtLagMs(v)
  if (key === 'downloadKbps') return fmtKbps(v)
  if (key === 'renderRatio') return fmtRatio(v)
  return `${v}`
}

function score(l: MetricLayer): number {
  const hasData = l.tier != null && l.valueText != null
  if (!hasData) return 1000
  // Critical(0) first/smallest/front → healthy(2) last/largest/back
  return l.tier as number
}

/** Build severity-ordered layers (worst → best) from a search/demo tick. */
export function layersFromTick(t: unknown): MetricLayer[] {
  const tick = t as Record<string, unknown> | null | undefined
  const m = tick?.metrics as Record<string, { tier?: Tier | null; displayValue?: number | null }> | undefined
  if (m && typeof m === 'object' && 'demuxInRate' in m) {
    const renderRatio =
      typeof m.renderRatio?.displayValue === 'number' && Number.isFinite(m.renderRatio.displayValue)
        ? m.renderRatio.displayValue
        : null
    // E2E slot is reserved for true media-path latency — never fall back to
    // pipeline/infra latency (that lives on the Topbar).
    const mediaRaw = m.mediaLatency?.displayValue
    const media =
      typeof mediaRaw === 'number' && Number.isFinite(mediaRaw) ? mediaRaw : null
    const layers: MetricLayer[] = [
      {
        key: 'demuxInRate',
        label: 'Demux rate',
        tier: m.demuxInRate?.tier ?? null,
        valueText: fmt(m.demuxInRate?.displayValue ?? null, 'demuxInRate', m.demuxInRate?.tier ?? null),
      },
      {
        key: 'demuxToDecodeLagMs',
        label: 'Demux→Db lag',
        tier: m.demuxToDecodeLag?.tier ?? null,
        valueText: fmt(m.demuxToDecodeLag?.displayValue ?? null, 'demuxToDecodeLagMs'),
      },
      {
        key: 'renderRatio',
        label: 'Render in/out',
        tier: m.renderRatio?.tier ?? null,
        valueText: fmt(renderRatio, 'renderRatio'),
      },
      {
        key: 'downloadKbps',
        label: 'Download',
        tier: m.downloadKbps?.tier ?? null,
        valueText: fmt(m.downloadKbps?.displayValue ?? null, 'downloadKbps'),
      },
      {
        key: 'e2eLatency',
        label: 'E2E latency',
        tier: m.mediaLatency?.tier ?? null,
        valueText: fmt(media, 'e2eLatency'),
      },
    ]
    return layers.sort((a, b) => score(a) - score(b))
  }

  const legacy = tick as {
    renderRatio?: number
    renderInOutGap?: number
    demuxInRateTier?: Tier | null
    demuxInRate?: number
    demuxToDecodeLagTier?: Tier | null
    demuxToDecodeLagMs?: number
    renderRatioTier?: Tier | null
    renderInOutGapTier?: Tier | null
    downloadKbpsTier?: Tier | null
    downloadKbps?: number
    mediaLatencyTier?: Tier | null
    mediaLatencyMs?: number
  } | null | undefined

  const renderRatio = (typeof legacy?.renderRatio === 'number' && Number.isFinite(legacy.renderRatio))
    ? legacy.renderRatio
    : (typeof legacy?.renderInOutGap === 'number' && Number.isFinite(legacy.renderInOutGap))
      ? legacy.renderInOutGap
      : null

  const layers: MetricLayer[] = [
    { key: 'demuxInRate', label: 'Demux rate', tier: legacy?.demuxInRateTier ?? null, valueText: fmt(legacy?.demuxInRate ?? null, 'demuxInRate', legacy?.demuxInRateTier ?? null) },
    { key: 'demuxToDecodeLagMs', label: 'Demux→Db lag', tier: legacy?.demuxToDecodeLagTier ?? null, valueText: fmt(legacy?.demuxToDecodeLagMs ?? null, 'demuxToDecodeLagMs') },
    { key: 'renderRatio', label: 'Render in/out', tier: legacy?.renderRatioTier ?? legacy?.renderInOutGapTier ?? null, valueText: fmt(renderRatio, 'renderRatio') },
    { key: 'downloadKbps', label: 'Download', tier: legacy?.downloadKbpsTier ?? null, valueText: fmt(legacy?.downloadKbps ?? null, 'downloadKbps') },
    { key: 'e2eLatency', label: 'E2E latency', tier: legacy?.mediaLatencyTier ?? null, valueText: fmt(legacy?.mediaLatencyMs ?? null, 'e2eLatency') },
  ]
  return layers.sort((a, b) => score(a) - score(b))
}

/**
 * Severity stats for a hex, used to order the grid.
 * `severeCount` = layers at critical tier (0); `avgTier` = mean tier of the
 * layers that have data (lower = worse); `hasData` flags all-empty hexes so they
 * sink to the bottom.
 */
export function hexSeverityStats(tick: unknown): {
  severeCount: number
  warnCount: number
  avgTier: number
  hasData: boolean
} {
  const layers = layersFromTick(tick)
  let severeCount = 0
  let warnCount = 0
  let sum = 0
  let n = 0
  for (const l of layers) {
    if (l.tier == null || l.valueText == null) continue
    n += 1
    sum += l.tier
    if (l.tier === 0) severeCount += 1
    else if (l.tier === 1) warnCount += 1
  }
  return { severeCount, warnCount, avgTier: n > 0 ? sum / n : Number.POSITIVE_INFINITY, hasData: n > 0 }
}

/**
 * Order hexes worst → best for the grid:
 *  1. more severe (critical, tier 0) layers first
 *  2. lower average layer tier first (tie-break when severe counts match;
 *     no-data hexes sink to the bottom)
 *  3. label/id ascending for a stable final order
 */
export function orderHexItems<T extends { id: string; label?: string; tick: unknown }>(
  items: T[],
): T[] {
  return items
    .map((item) => ({ item, stats: hexSeverityStats(item.tick) }))
    .sort((a, b) => {
      if (b.stats.severeCount !== a.stats.severeCount) return b.stats.severeCount - a.stats.severeCount
      if (a.stats.avgTier !== b.stats.avgTier) return a.stats.avgTier - b.stats.avgTier
      const al = (a.item.label ?? a.item.id).toLowerCase()
      const bl = (b.item.label ?? b.item.id).toLowerCase()
      return al.localeCompare(bl)
    })
    .map((entry) => entry.item)
}

export function nodeCount(tick: unknown): number {
  const t = tick as {
    totalStreams?: number
    kind?: string
    criticalCount?: number
    warningCount?: number
    goodCount?: number
  } | null | undefined

  // Stream tiles always count as 1 presence (including no-data / soft-retain shells).
  if (t?.kind === 'stream') return 1

  const total =
    typeof t?.totalStreams === 'number' && Number.isFinite(t.totalStreams)
      ? Math.max(0, Math.round(t.totalStreams))
      : null

  // Prefer server totalStreams when it looks like a real active count.
  if (total != null && total > 0) return total

  // Fallback: sum scored-stream tallies when totalStreams is missing or zero.
  const hasSpread =
    typeof t?.criticalCount === 'number' ||
    typeof t?.warningCount === 'number' ||
    typeof t?.goodCount === 'number'
  if (hasSpread) {
    const sum =
      Math.max(0, Math.round(Number(t?.criticalCount ?? 0))) +
      Math.max(0, Math.round(Number(t?.warningCount ?? 0))) +
      Math.max(0, Math.round(Number(t?.goodCount ?? 0)))
    if (sum > 0) return sum
  }

  if (total != null) return total
  // Group tiles must not fall through to 1 when totals are absent.
  return 0
}
